import { useToast } from '@act/react-components/app/ReusableToast';
import {
  CallDto,
  SuggestedActionDto,
  SuggestedNeedDto,
  SuggestedScheduledEventDto,
  SuggestionsDto,
} from '@act/shared/data-transfer-objects';
import {
  Button,
  FlexContainer,
  IconLibrary,
  ReemaThemeV2,
} from '@act/storybook';
import { metaSliceSelectors } from 'redux/slices/metaSlice.hook';
import { Box, IconButton, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { callsRequests } from 'redux/requests/calls.request';
import { useApiCall } from 'redux/utils/apiHook.hook';
import NegativeQuestion from '../../post-call-summary-drawer/post-call-feedback/NegativeQuestion';
import { PostCallFeedbackState } from '../../post-call-summary-drawer/post-call-feedback/types';
import { INITIAL_POST_CALL_FEEDBACK_STATE } from '../../post-call-summary-drawer/post-call-feedback/initialPostCallFeedbackState.const';
import { TrackingProvider, useTracking } from '@act/react-components/tracking';
import { callsAppSelectors } from 'redux/redux';
import { removeHtmlTags } from '@act/shared/utilities';

type SuggestionType = 'need' | 'action' | 'scheduled_event';
type SuggestionDto =
  | SuggestedNeedDto
  | SuggestedActionDto
  | SuggestedScheduledEventDto;

interface Props {
  suggestions: SuggestionsDto;
  setOpenAddSuggestedNeed: (need: SuggestedNeedDto | null) => void;
  setOpenAddAction: (action: SuggestedActionDto | null) => void;
  setOpenAddScheduledEvent: (event: SuggestedScheduledEventDto | null) => void;
  call: CallDto;
}

interface SuggestionCardProps {
  id?: string;
  type: SuggestionType;
  title: string;
  description?: string;
  onClick: () => void;
  shortTitle?: string;
  typeLabel?: string;
  onThumbClick: (value: boolean) => void;
  generateTrackingProps: (actionName: string) => Record<string, string>;
  showNegativeFeedback: boolean;
  setShowNegativeFeedback: (value: boolean) => void;
  postCallFeedback: PostCallFeedbackState;
  setPostCallFeedback: (feedback: PostCallFeedbackState) => void;
  submitFeedback: (feedback?: PostCallFeedbackState) => Promise<void>;
  isUpdatingPostCallFeedback: boolean;
  originalSuggestion: SuggestionDto;
}

type SuggestionItem = {
  id?: string;
  deletedAt?: number;
  key: string;
  type: SuggestionType;
  title: string;
  description?: string;
  shortTitle?: string;
  typeLabel?: string;
  originalSuggestion?: SuggestionDto;
};

const CONTAINER_STYLES = {
  backgroundColor: ReemaThemeV2.LIGHT_MODE.palette.greyShades[100],
  border: `1px solid ${ReemaThemeV2.LIGHT_MODE.palette.greyShades[250]}`,
} as const;

const normalizeText = (description: string): string => {
  if (!description) return '';
  return removeHtmlTags(description).replace(/&nbsp;/g, ' ');
};

const SuggestionCard = ({
  title,
  description,
  onClick,
  shortTitle,
  typeLabel,
  onThumbClick,
  showNegativeFeedback,
  setShowNegativeFeedback,
  postCallFeedback,
  setPostCallFeedback,
  submitFeedback,
  isUpdatingPostCallFeedback,
  originalSuggestion,
  generateTrackingProps,
}: SuggestionCardProps) => {
  const showFeedbackButtons =
    !showNegativeFeedback && originalSuggestion?.isAccurateSuggestion === null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '16px',
        ...CONTAINER_STYLES,
        borderRadius: '12px',
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography
            sx={{ alignSelf: 'center' }}
            variant="titleMedium"
            fontWeight={600}
          >
            {title}
          </Typography>
          <Button
            variant="text"
            onClick={onClick}
            {...generateTrackingProps('add-suggestion-button')}
            startIcon={<IconLibrary iconName="Plus" weight="regular" />}
          >
            Add {typeLabel}
          </Button>
        </Box>

        {shortTitle && (
          <Box sx={{ mb: 1 }}>
            <Typography
              variant="bodySmall"
              sx={{
                color: ReemaThemeV2.LIGHT_MODE.palette.greyShades[800],
              }}
            >
              {shortTitle}
            </Typography>
          </Box>
        )}

        {description && (
          <Typography
            variant="bodySmall"
            sx={{
              color: ReemaThemeV2.LIGHT_MODE.palette.greyShades[600],
              lineHeight: 1.4,
            }}
          >
            {description}
          </Typography>
        )}

        {showFeedbackButtons && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="bodySmall">
              Was this suggestion accurate?
            </Typography>
            <Box>
              <IconButton
                {...generateTrackingProps('thumbs-up-feedback-button')}
                onClick={() => onThumbClick(true)}
              >
                <IconLibrary
                  iconName="ThumbsUp"
                  weight="regular"
                  size={20}
                  color="secondary"
                />
              </IconButton>
              <IconButton
                {...generateTrackingProps('thumbs-down-feedback-button')}
                onClick={() => onThumbClick(false)}
              >
                <IconLibrary
                  iconName="ThumbsDown"
                  weight="regular"
                  size={20}
                  color="secondary"
                />
              </IconButton>
            </Box>
          </Box>
        )}

        {showNegativeFeedback && (
          <Box sx={{ width: '100%', mt: 2 }}>
            <NegativeQuestion
              feedbackType="suggestions"
              postCallFeedback={postCallFeedback}
              isUpdatingPostCallFeedback={isUpdatingPostCallFeedback}
              setPostCallFeedback={setPostCallFeedback}
              submitFeedback={submitFeedback}
              onClose={() => setShowNegativeFeedback(false)}
              compact
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

const mapNeedToSuggestion = (
  need: SuggestedNeedDto,
  needMetadata: ReturnType<typeof metaSliceSelectors.useSelectNeedsMetadata>
): SuggestionItem => {
  const issueLabel = need?.issueId
    ? needMetadata.data.issues.find(i => i.id === need.issueId)?.label
    : undefined;
  return {
    id: need.id,
    key: need.id || need.tempNeedId,
    type: 'need',
    typeLabel: 'need',
    title: issueLabel ?? 'Suggested Need',
    shortTitle: 'New need discussed',
    description: normalizeText(need?.reason),
    originalSuggestion: need,
  };
};

const mapActionToSuggestion = (
  action: SuggestedActionDto,
  needMetadata: ReturnType<typeof metaSliceSelectors.useSelectNeedsMetadata>
): SuggestionItem => {
  const issueLabel = action.need?.issueId
    ? needMetadata.data.issues.find(i => i.id === action.need?.issueId)?.label
    : undefined;
  const activityLabel = action?.activityId
    ? needMetadata.data.activities.find(a => a.id === action?.activityId)?.label
    : undefined;

  return {
    id: action.id,
    key: action.id || `action-${action.activityId}`,
    type: 'action',
    typeLabel: 'action',
    title: issueLabel,
    shortTitle: activityLabel,
    description: normalizeText(action.description),
    originalSuggestion: action,
  };
};

const mapEventToSuggestion = (
  event: SuggestedScheduledEventDto
): SuggestionItem => ({
  id: event.id,
  key: event.id || `event-${event?.type}`,
  type: 'scheduled_event',
  typeLabel: 'scheduled event',
  title: `Schedule ${event?.type}`,
  description: normalizeText(event.note),
  originalSuggestion: event,
  shortTitle: event?.deletedAt ? normalizeText(event?.note) : null,
});

export const LlmSuggestions = ({
  suggestions,
  setOpenAddSuggestedNeed,
  setOpenAddAction,
  setOpenAddScheduledEvent,
  call,
}: Props) => {
  const toast = useToast();
  const { generateTrackingProps } = useTracking();
  const [showNegativeFeedbackFor, setShowNegativeFeedbackFor] = useState<
    string | null
  >(null);
  const needMetadata = metaSliceSelectors.useSelectNeedsMetadata();
  const currentCall = callsAppSelectors.useSelectCallById(call?.id);
  const suggestionsLoadingStatus =
    callsAppSelectors.useSelectSuggestionsLoadingStatusByCallId(call?.id);
  const [
    createdSuggestionAccuracyFeedback,
    loadingCreateSuggestionAccuracyFeedback,
    createSuggestionAccuracyFeedback,
  ] = useApiCall(callsRequests.createSuggestionAccuracyFeedback);
  const [
    undoneSuggestion,
    loadingUndoCreatedSuggestion,
    undoCreatedSuggestion,
    undoCreatedSuggestionError,
  ] = useApiCall(callsRequests.undoCreatedSuggestion);
  const [postCallFeedback, setPostCallFeedback] =
    useState<PostCallFeedbackState>(INITIAL_POST_CALL_FEEDBACK_STATE);

  useEffect(() => {
    if (createdSuggestionAccuracyFeedback) {
      toast('Thank you for your feedback!');
    }
  }, [createdSuggestionAccuracyFeedback]);

  const allSuggestions: SuggestionItem[] = suggestions
    ? [
        ...suggestions?.needs.map(need =>
          mapNeedToSuggestion(need, needMetadata)
        ),
        ...suggestions?.actions.map(action =>
          mapActionToSuggestion(action, needMetadata)
        ),
        ...suggestions?.scheduledEvents.map(mapEventToSuggestion),
      ]
    : [];

  const handleClick = (type: SuggestionType, suggestion: SuggestionDto) => {
    switch (type) {
      case 'need':
        setOpenAddSuggestedNeed(suggestion as SuggestedNeedDto);
        break;
      case 'action':
        setOpenAddAction(suggestion as SuggestedActionDto);
        break;
      case 'scheduled_event':
        setOpenAddScheduledEvent(suggestion as SuggestedScheduledEventDto);
        break;
    }
  };

  const handleUndo = (suggestion: SuggestionItem) => {
    undoCreatedSuggestion({
      params: {
        id: call.id,
        suggestionType: suggestion.type,
        suggestionId: suggestion.id,
      },
    });
  };

  useEffect(() => {
    if (undoneSuggestion) {
      toast('Suggestion undone successfully', 'success');
    }
    if (undoCreatedSuggestionError) {
      toast('Failed to undo suggestion. Please try again.', 'error');
    }
  }, [undoneSuggestion, undoCreatedSuggestionError]);

  const handleThumbClick = (suggestion: SuggestionItem, value: boolean) => {
    if (value) {
      createSuggestionAccuracyFeedback({
        params: { suggestionId: suggestion.id },
        body: {
          isAccurateSuggestion: value,
          type: suggestion.type,
        },
      });
      setShowNegativeFeedbackFor(null);
      setPostCallFeedback(INITIAL_POST_CALL_FEEDBACK_STATE);
    } else {
      setPostCallFeedback({
        ...INITIAL_POST_CALL_FEEDBACK_STATE,
        isAccurateSuggestion: false,
      });
      setShowNegativeFeedbackFor(suggestion.key);
    }
  };

  const handleSubmitFeedback = async (
    suggestion: SuggestionItem,
    feedback?: PostCallFeedbackState
  ) => {
    const feedbackToSubmit = feedback || postCallFeedback;
    const accuracyIssuesString =
      feedbackToSubmit.suggestionAccuracyIssues?.length > 0
        ? feedbackToSubmit.suggestionAccuracyIssues.join(',')
        : undefined;

    try {
      createSuggestionAccuracyFeedback({
        params: { suggestionId: suggestion.id },
        body: {
          isAccurateSuggestion: false,
          type: suggestion.type,
          suggestionAccuracyIssues: accuracyIssuesString
            ? [accuracyIssuesString]
            : undefined,
          suggestionAccuracyFeedback:
            feedbackToSubmit.suggestionAccuracyFeedback,
        },
      });
      setShowNegativeFeedbackFor(null);
      setPostCallFeedback(INITIAL_POST_CALL_FEEDBACK_STATE);
    } catch (error) {
      toast('Failed to submit feedback. Please try again.', 'error');
    }
  };

  const noSuggestedActions =
    !!suggestions &&
    allSuggestions?.length === 0 &&
    currentCall?.isLlmSuggestionsProcessed;
  const suggestedActionsLoading =
    !currentCall?.isLlmSuggestionsProcessed ||
    (currentCall?.isLlmSuggestionsProcessed &&
      (suggestionsLoadingStatus === 'loading' ||
        suggestionsLoadingStatus === 'initial'));
  if (noSuggestedActions || suggestedActionsLoading) {
    return (
      <FlexContainer
        borderRadius="lessRounded"
        direction="column"
        gap={4}
        padding={2}
        width="100%"
        height="100%"
        {...CONTAINER_STYLES}
      >
        <Typography variant="titleLarge" fontWeight={600}>
          Suggested Actions
        </Typography>
        <Typography
          variant="bodyMedium"
          sx={{ color: ReemaThemeV2.LIGHT_MODE.palette.greyShades[600] }}
        >
          {suggestedActionsLoading
            ? 'Processing the call to look for suggestions…'
            : 'There were no suggestions generated for this call.'}
        </Typography>
      </FlexContainer>
    );
  }
  let addedSuggestions: SuggestionItem[] = [];
  let unaddedSuggestions: SuggestionItem[] = [];
  allSuggestions.forEach(suggestion => {
    if (suggestion.originalSuggestion?.deletedAt) {
      addedSuggestions.push(suggestion);
    } else {
      unaddedSuggestions.push(suggestion);
    }
  });

  return (
    <TrackingProvider contextName="post-call-llm-suggestions">
      <FlexContainer
        borderRadius="lessRounded"
        direction="column"
        gap={4}
        padding={2}
        width="100%"
        height="100%"
        {...CONTAINER_STYLES}
      >
        <Typography variant="titleLarge" fontWeight={600}>
          Suggested Actions
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            overflowY: 'auto',
            flex: 1,
          }}
        >
          {addedSuggestions.map(suggestion => (
            <Box
              key={`${suggestion.id}-${suggestion.key}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '16px',
                ...CONTAINER_STYLES,
                borderRadius: '12px',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography
                    sx={{ alignSelf: 'center' }}
                    variant="titleMedium"
                    fontWeight={600}
                  >
                    {suggestion.title}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      justifyContent: 'center',
                    }}
                  >
                    <Button
                      sx={{ marginLeft: '12px', alignSelf: 'flex-end' }}
                      variant="text"
                      onClick={() => handleUndo(suggestion)}
                      disabled={loadingUndoCreatedSuggestion}
                      startIcon={
                        <IconLibrary
                          iconName="ArrowUUpLeftIcon"
                          weight="regular"
                          size={20}
                        />
                      }
                    >
                      Undo
                    </Button>
                    <IconLibrary iconName="Check" weight="regular" size={20} />{' '}
                    <Typography variant="labelLarge" textAlign="center">
                      Added
                    </Typography>
                  </Box>
                </Box>
                {suggestion.shortTitle && (
                  <Box sx={{ mb: 1 }}>
                    <Typography
                      variant="bodySmall"
                      sx={{
                        color: ReemaThemeV2.LIGHT_MODE.palette.greyShades[800],
                      }}
                    >
                      {suggestion.shortTitle}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          ))}
          {unaddedSuggestions.map(suggestion => (
            <SuggestionCard
              key={suggestion.key}
              id={suggestion.id}
              type={suggestion.type}
              title={suggestion.title}
              typeLabel={suggestion.typeLabel}
              description={suggestion.description}
              shortTitle={suggestion.shortTitle}
              generateTrackingProps={generateTrackingProps}
              originalSuggestion={suggestion.originalSuggestion}
              onClick={() =>
                handleClick(suggestion.type, suggestion.originalSuggestion)
              }
              onThumbClick={value => handleThumbClick(suggestion, value)}
              showNegativeFeedback={showNegativeFeedbackFor === suggestion.key}
              setShowNegativeFeedback={value =>
                setShowNegativeFeedbackFor(value ? suggestion.key : null)
              }
              postCallFeedback={postCallFeedback}
              setPostCallFeedback={setPostCallFeedback}
              submitFeedback={async feedback =>
                handleSubmitFeedback(suggestion, feedback)
              }
              isUpdatingPostCallFeedback={
                loadingCreateSuggestionAccuracyFeedback
              }
            />
          ))}
        </Box>
      </FlexContainer>
    </TrackingProvider>
  );
};
